import { getFilterCond } from '@opm/services';
import { ICondHooksProps, TRequestKeys } from '../types';
import * as config from '../config';
import { useEffect, useMemo, useState } from 'react';
import {
  getCondStorageKey,
  getFilterCondConfig,
  saveFilterCondConfig,
} from '../utils/storage';
import { useTranslateConfig } from '@opm/hooks/useTranslateConfig';
import { useFilterCondGameIds } from '../context/FilterCondGameIdsContext';

export function useFilterCondData({
  keys,
  backCondConfig,
  extrRequestKeys = [],
  condGameIds: condGameIdsProp,
}: ICondHooksProps) {
  const translateContext = useTranslateConfig();
  const contextCondGameIds = useFilterCondGameIds();
  const condGameIds = condGameIdsProp ?? contextCondGameIds;
  const condGameIdsKey = useMemo(
    () => (condGameIds?.length ? [...condGameIds].sort().join(',') : ''),
    [condGameIds],
  );
  const storageKey = useMemo(
    () => getCondStorageKey(condGameIds),
    [condGameIdsKey],
  );
  const [loading, setLoading] = useState(true);
  const [condData, setCondData] = useState<{ [key: string]: any }>({});

  function getRequestKeys() {
    const requestKeys: TRequestKeys[] = [...extrRequestKeys];

    keys.forEach(key => {
      const { requestKey } = config[key];

      if (requestKey) {
        requestKeys.push(requestKey);
      }
    });

    return { requestKeys };
  }

  function getStorageCond(keys: Array<string>) {
    const condData = getFilterCondConfig(storageKey);
    const newCondData: any = {};
    const newRequestKeys: Array<string> = [];
    keys.forEach((key: string) => {
      const data = condData[key];
      if (data) {
        newCondData[key] = data;
      } else {
        newRequestKeys.push(key);
      }
    });

    return { newCondData, newRequestKeys };
  }

  function requestCond() {
    setLoading(true);
    const { requestKeys } = getRequestKeys();
    const { newCondData, newRequestKeys } = getStorageCond(requestKeys);

    if (newRequestKeys.length === 0) {
      // 自动处理翻译配置（如果有 Provider）
      translateContext?.setConfig?.(newCondData);
      // 用户自定义回调依然生效
      backCondConfig?.(newCondData);
      setCondData(newCondData);
      setLoading(false);
      return;
    }

    const payload: {
      request_types: string[];
      game_ids?: string[];
    } = { request_types: newRequestKeys };
    if (condGameIds?.length) {
      payload.game_ids = condGameIds;
    }

    getFilterCond(payload)
      .then((data: any) => {
        saveFilterCondConfig(data, storageKey);
        const info = { ...data, ...newCondData };
        // 自动处理翻译配置（如果有 Provider）
        translateContext?.setConfig?.(info);
        // 用户自定义回调依然生效
        backCondConfig?.(info);
        setCondData(info);
      })
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => setLoading(false));
  }

  // 仅随 cond game_ids 分桶变化重拉；避免依赖 translateContext 导致无限请求
  useEffect(() => {
    requestCond();
  }, [storageKey]);

  return { loading, condData };
}
